import { useEffect, useState } from 'react';
import { mensajeDeError } from '@/core/red/conectividad';
import { acceptInvitation, fetchMyInvitations, type PendingInvitation } from './organizations';
import { useOrg } from './OrgProvider';

/**
 * «Te invitaron a una veterinaria».
 *
 * ## Por qué esto importa tanto
 *
 * Invitar a alguien tiene dos mitades: crear la invitación y avisarle. El
 * defecto original fue confundirlas —la pantalla decía «Invitación enviada»
 * con sólo haber insertado una fila— y se arregló mandando el correo de
 * verdad. Pero quedó una tercera mitad que nadie miró: **que la persona la
 * vea al entrar**.
 *
 * Este componente existía y no lo dibujaba nadie. Ningún lugar de la
 * aplicación lo montaba. Así que la invitación se creaba, el correo salía, y
 * quien entraba a buscarla no encontraba nada en ninguna pantalla.
 *
 * Peor todavía en el caso más común, que es el que lo destapó: cuando la
 * persona **ya tiene cuenta**, Supabase no manda ningún correo —no se puede
 * invitar a quien ya está registrado— y la aplicación lo informaba diciendo
 * «cuando entre con ese mismo correo va a ver la invitación esperándola».
 * No había dónde verla. Era una promesa que el programa no podía cumplir.
 *
 * ## Dónde se monta
 *
 * En los dos estados en que alguien puede recibir una invitación:
 *
 *   * **Sin veterinaria** — arriba del asistente de arranque. Es el caso
 *     típico: alguien a quien suman al equipo. Sin esto crea una veterinaria
 *     propia, duplicada y vacía, que es lo contrario de lo que le pidieron.
 *   * **Con veterinaria** — en Inicio, para quien ya trabaja en una y lo suman
 *     a otra.
 *
 * Cuando no hay ninguna invitación no dibuja nada.
 */
export function InvitationsCard({ sinVeterinaria = false }: { sinVeterinaria?: boolean } = {}) {
  const { refresh, setActiveOrg } = useOrg();
  const [invitations, setInvitations] = useState<PendingInvitation[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    fetchMyInvitations()
      .then(setInvitations)
      .catch((e) => setError(mensajeDeError(e)));
  };

  useEffect(load, []);

  if (invitations.length === 0) return null;

  const accept = async (inv: PendingInvitation) => {
    setBusy(inv.invitation_id);
    setError(null);
    try {
      await acceptInvitation(inv.invitation_id);
      await refresh();
      // Y se PASA a esa veterinaria. Sin esto, alguien que ya tenía la suya
      // aceptaba la invitación y no cambiaba nada en la pantalla: el selector
      // seguía mostrando la propia, porque recuerda la última elegida. La
      // conclusión razonable es «no funcionó», y no hay nada que la corrija.
      setActiveOrg(inv.organization_id);
      load();
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setBusy(null);
    }
  };

  const varias = invitations.length > 1;

  return (
    <section
      // El margen va acá y no en un envoltorio de quien la monta: así, cuando
      // no hay ninguna invitación, esto no deja un hueco arriba del asistente
      // ni del tablero.
      className={
        sinVeterinaria ? 'superficie mx-auto mt-5 w-full max-w-md p-4' : 'superficie mx-4 mt-4 p-4'
      }
      data-testid="invitations"
    >
      <h2 className="font-medium">
        {varias ? 'Te invitaron a estas veterinarias' : 'Te invitaron a una veterinaria'}
      </h2>

      {/* Quien todavía no tiene ninguna está mirando el asistente de arranque
          justo abajo. Sin esta línea, lo que ve son dos caminos sin saber que
          uno de los dos le cae encima el trabajo de armar todo de nuevo. */}
      {sinVeterinaria && (
        <p className="mt-1 text-sm text-[var(--tinta-suave)]" data-testid="invitations-nota">
          Si es a esto a lo que te sumaron, aceptá acá: entrás a la veterinaria que ya está armada y
          no hace falta que crees una propia.
        </p>
      )}

      {error && <p className="mt-2 text-sm text-[var(--peligro)]">{error}</p>}

      <ul className="mt-3 flex flex-col gap-3">
        {invitations.map((inv) => (
          <li key={inv.invitation_id} className="flex items-center justify-between gap-3">
            <span className="text-sm">
              <strong>{inv.organization_name}</strong>
              <span className="opacity-70">
                {' · '}
                {inv.role === 'admin' ? 'como administrador' : 'como miembro'}
              </span>
            </span>
            <button
              onClick={() => accept(inv)}
              disabled={busy === inv.invitation_id}
              className="min-h-11 shrink-0 rounded-[var(--radius-control)] bg-[var(--accion)] px-4 text-sm text-[var(--sobre-accion)] disabled:opacity-50"
              data-testid="accept-invitation"
            >
              {busy === inv.invitation_id ? 'Uniéndote…' : 'Unirme'}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
